import { FromCarActionBase } from '../from-car-action.base';
import {
  BookingParams,
  BookingPosition,
  BookingResult,
  StockTargetType,
} from '../../types';

/**
 * Car-only-Welt: Quelle KFZ.
 *
 * Befüllen (KFZ → Maschine) und Entsorgen (KFZ → Müll) sind movement-los und
 * ändern nur das lokale Fahrzeuglager (`CAR_STORAGE`); das Maschinenlager wird
 * nicht gebucht, die remote Car-Buchung läuft beim Auftragsabschluss im Backend.
 * KFZ → KFZ (Umlagerung) legt ein zweiphasiges CarRelocationMovement online an.
 * Andere Ziele sind in dieser Welt nicht verfügbar.
 */
export class CarOnlyFromCarAction extends FromCarActionBase {
  /**
   * Routet die Buchung nach `targetType` auf KFZ (Umlagerung), Maschine
   * (Befüllen) oder Müll (Entsorgen); andere Ziele sind in der Car-only-Welt
   * nicht verfügbar.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult der gewählten Richtung bzw. Fehler bei unzulässigem Ziel
   */
  public async execute(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    this.debugInfo('[from-car:car-only]', position, params);
    switch (params.context.booking.targetType) {
      case StockTargetType.CAR:
        return this.toCar(position, params);
      case StockTargetType.MACHINE:
        return this.toMachine(position, params);
      case StockTargetType.TRASH_BIN:
        return this.toTrashBin(position, params);
      default:
        return this.failure(
          `KFZ → ${params.context.booking.targetType} ist in dieser Welt nicht verfügbar`,
        );
    }
  }

  /**
   * KFZ → Maschine (Befüllen): movement-los, verringert nur das lokale
   * Fahrzeuglager um amount und reiht die Buchung für den Auftrags-Rollback in
   * die Transaction ein.
   *
   * @param position Zu buchende Position (article_id und amount; amount darf negativ sein, nur nicht 0)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler ohne lokales Fahrzeuglager, bei amount == 0 oder Update-Fehlschlag
   */
  protected async toMachine(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { adapters } = params;
    if (!adapters.car.hasLocalInventory()) {
      return this.failure('KFZ->Maschine: kein lokales Fahrzeuglager vorhanden');
    }
    // amount darf negativ sein (verrechneter Take-Back-Saldo, Rückgabe ans KFZ);
    // nur 0 ist sinnlos.
    if (position.amount === 0) {
      return this.failure('KFZ->Maschine: amount darf nicht 0 sein');
    }
    // In die Transaction einreihen (für den Auftrags-Rollback). Movement-los:
    // beim Abbruch bucht der Modul-Rollback die Entnahme gegen und stellt das
    // Fahrzeuglager wieder her. Die Gegenbuchung trägt keine transactionId und
    // reiht sich daher nicht erneut ein.
    const rollbackFailure = this.enqueueForRollback(position, params);
    if (rollbackFailure) {
      return rollbackFailure;
    }
    const carUpdated = await adapters.car.updatePositionAmountLocal(
      position,
      -position.amount,
    );
    if (!carUpdated) {
      return this.failure('Car-Bestand konnte nicht aktualisiert werden');
    }
    return this.success();
  }

  /**
   * KFZ → Müll (Entsorgung): movement-los, schreibt das lokale
   * `disposal_articles`-Lager des Fahrzeugs und reiht die Buchung für den
   * Auftrags-Rollback in die Transaction ein. Die Backend-Buchung läuft beim
   * Auftragsabschluss.
   *
   * @param position Zu buchende Position (article_id und amount; amount darf negativ sein, nur nicht 0)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler ohne lokales Fahrzeuglager, bei amount == 0 oder Update-Fehlschlag
   */
  protected async toTrashBin(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { adapters } = params;
    if (!adapters.car.hasLocalInventory()) {
      return this.failure('KFZ->Müll: kein lokales Fahrzeuglager vorhanden');
    }
    // amount darf negativ sein (Rollback-Gegenbuchung holt die entsorgte Menge
    // zurück); nur 0 ist sinnlos.
    if (position.amount === 0) {
      return this.failure('KFZ->Müll: amount darf nicht 0 sein');
    }
    // In die Transaction einreihen (für den Auftrags-Rollback). Movement-los:
    // beim Abbruch bucht der Modul-Rollback die Entsorgung gegen und stellt das
    // Disposal-Lager wieder her. Die Gegenbuchung trägt keine transactionId und
    // reiht sich daher nicht erneut ein.
    const rollbackFailure = this.enqueueForRollback(position, params);
    if (rollbackFailure) {
      return rollbackFailure;
    }
    const disposalUpdated = await adapters.car.updateDisposalAmountLocal(
      position,
      -position.amount,
    );
    if (!disposalUpdated) {
      return this.failure('Car-Disposal konnte nicht aktualisiert werden');
    }
    return this.success();
  }
}
